import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, DragEvent, JSX, KeyboardEvent, MouseEvent } from 'react';
import {
  Button,
  IconBulletList,
  IconCalendarMark,
  IconChevronRight,
  IconPlus,
  REPLI_PERSONNE_INCONNUE,
  ViewSelector,
  cn,
  couleurCss,
  peutEcrireSurPorte,
  titreDePersonne,
  useAccesPort,
  usePersonnesPort,
} from '@taki/ui';
import type { PersonneVue } from '@taki/ui';
import { useAppRuntime } from '@taki/app-runtime';
import type { ProprietesDeCanvas } from '@taki/app-runtime';
import { useUser } from '@taki/auth';
import { contactDisplayName, estCouleur, jourDansFuseau } from '@taki/data-model';
import type { Couleur, TimesheetCreateInput, TimesheetUpdateInput } from '@taki/data-model';
import { useTakiApp } from '../../../App.js';
import { AgendaPicker } from './AgendaPicker.js';
import { EventEditor } from './EventEditor.js';
import type { OptionDeProjet, OptionDeTache, SaisieDeCreneau } from './EventEditor.js';
import { fermeAuBlur } from './fermeture-menu.js';
import { GoogleAgendaButton, GoogleAllDayRow, GoogleEventCard } from './GoogleAgendaUi.js';
import { useGoogleAgenda } from './googleAgenda.js';
import { lireCatalogue } from './catalogue-semaine.js';
import { couleurDuCreneau } from './teinte-creneau.js';
import { DECALAGE_DE_COPIE, copieDeCreneau, positionDuMenu, profondeursDeCascade } from './gestes-creneau.js';
import { champsAChoixDuJeu, motDeLaValeur, titreDuJeu } from '../catalogues.js';
import {
  JOURS,
  JOURS_LONGS,
  addDays,
  calerMinutes,
  creneauxDeLaSemaine,
  hoursLabel,
  instantDuCreneau,
  instantDuJour,
  minutesDeDuree,
  mondayOf,
  overlaps,
  personneDeFiche,
  photoAvecClefs,
  photoDeFiche,
  timeLabel,
  weekKey,
  weekLabel,
} from './journal.js';
import type { CreneauSemaine } from './journal.js';

/**
 * MA SEMAINE : la surface de la porte SPACE CANVAS de Timesheet (regle G23,
 * doctrine dans `docs/GABARIT-ECRAN.md` section 9).
 *
 * POURQUOI UNE PORTE ET PAS UNE VUE. Une vue est une LENTILLE sur une
 * collection : elle a des lignes, une identite de ligne et le geste « ouvrir ».
 * Un planning de semaine n'a rien de tout ca. Il a une SEMAINE, des creneaux
 * qu'on pose, et il ne veut ni tri ni favoris a cote de lui. Il prend donc la
 * porte entiere : la rangee 3 est absorbee, le tiroir (zone 4) aussi.
 *
 * CE QUE LA PORTE GARDE DU GABARIT : le rail (zone 1) et l'EN-TETE ENTIER
 * (zone 2), donc le titre, la RECHERCHE et les STATISTIQUES. La rangee 3 ne
 * garde RIEN (`canvas: {}` dans `taki.screens.json`), et le bouton « Ajouter »
 * descend DANS la surface, la ou le geste se fait.
 *
 * ELLE LIT ET ELLE ECRIT LE JOURNAL, par le SDK, et c'est tout ce qui a change
 * depuis la PR 137. L'apercu en memoire n'existe plus : plus de `INITIAL_BLOCKS`,
 * plus de semaine de demonstration, plus de « modifications non enregistrees ».
 * Un creneau EST une fiche de la famille `timesheets` (type `journal`) : poser,
 * deplacer, redimensionner, modifier et supprimer sont cinq ecritures reelles,
 * et « Annuler » en est une sixieme, qui reecrit l'etat d'avant plutot que de
 * remettre un tableau local en place.
 *
 * L'ETAT LOCAL SERT LE GESTE, PAS LA VERITE. Le socle ne recharge pas la porte
 * apres une ecriture : la surface garde donc les fiches de sa porte en etat,
 * les resynchronise quand la prop change, et les remplace par ce que le SDK
 * REND apres chaque ecriture. Une ecriture qui echoue remet l'etat d'avant et
 * dit sa raison dans le pied ; elle ne laisse jamais un ecran blanc ni un
 * creneau qui a bouge sans etre enregistre.
 *
 * LA RECHERCHE DE LA ZONE 2b EST BRANCHEE, et ce n'est pas de la decoration :
 * le gabarit rend la case, mais personne ne filtre a notre place sur un canvas.
 * Elle reduit les creneaux affiches sur leur titre (la tache), le projet et le client.
 *
 * CE QUI RESTE INTERDIT, hors gabarit comme dedans : pas de Firebase en direct
 * (tout passe par `@taki/sdk`), pas de routeur, pas de couleur en dur, pas de
 * `px` fantaisiste hors des positions calculees de la grille : tout vient des
 * tokens de `@taki/ui`. Cette surface ne redessine AUCUNE zone du gabarit (G23),
 * pas meme le « Ajouter » de la rangee 3a, qui n'existe pas sur cette porte.
 *
 * LE DOUBLE-CLIC SUR LA GRILLE POSE UN CRENEAU LA OU L'ON VISE, comme dans un
 * agenda : il ouvre la meme fenetre que « Ajouter », deja reglee sur le jour
 * de la colonne et sur le quart d'heure pointe, pour une heure. Rien n'est
 * ecrit avant « Enregistrer ». Le bouton reste le chemin du clavier (G16).
 *
 * LE CLIC DROIT SUR UN CRENEAU ouvre un menu de deux gestes a la place de celui
 * du navigateur : « Dupliquer » pose une copie identique (meme tache, meme
 * projet, memes renseignements, meme duree) un quart d'heure plus tard, et
 * « Archiver » passe par l'archivage habituel, que le pied sait annuler. Une
 * copie recouvre son original : les creneaux d'un meme jour qui se chevauchent
 * se decalent en cascade pour que chacun se lise. Sans droit d'ecriture sur la
 * porte, le menu s'ouvre quand meme et ses deux choix restent GRISES, avec leur
 * raison (`gestes-creneau.ts` porte la copie, la cascade et la place du menu).
 *
 * L'essai Google Agenda, en lecture seule et sans rien enregistrer, est decrit
 * dans `googleAgenda.ts` ; les conversions fiche <-> creneau dans `journal.ts`.
 */

/** Une fiche du journal telle que le socle et le SDK la rendent. */
type Fiche = Record<string, unknown>;

const START_HOUR = 0;
const END_HOUR = 24;
/** L'heure en haut de la grille a l'ouverture : le debut d'une journee de travail. */
const HEURE_D_ARRIVEE = 7;
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, index) => START_HOUR + index);
/** Le pas de la grille, en heures : cinq minutes. */
const STEP = 1 / 12;
const snap = (hour: number): number => Math.round(hour * 12) / 12;
/**
 * Le pas du DOUBLE-CLIC, en minutes : un quart d'heure, arrondi vers le bas.
 * Au pas de cinq minutes, viser 9 h donnait 9 h 05 une fois sur deux ; au quart
 * d'heure, le creneau commence sur la ligne ou dans la case que l'on voit.
 */
const QUART_D_HEURE = 15;
/** Les colonnes de la grille : la gouttiere des heures, puis les jours. */
const WEEKDAY_COLUMNS = 'grid-cols-[3.5rem_repeat(5,minmax(7.8125rem,1fr))]';
const FULL_WEEK_COLUMNS = 'grid-cols-[3.5rem_repeat(7,minmax(6rem,1fr))]';

type DragPayload = { kind: 'block'; blockId: string };

/**
 * La teinte d'un creneau, rendue depuis sa couleur (`couleurDuCreneau`, qui
 * choisit celle du projet ou, a defaut, une teinte stable tiree d'une graine).
 * Fond melange a la surface, bord plus soutenu : le texte reste lisible.
 */
function tonDuCreneau(couleur: Couleur): CSSProperties {
  const css = couleurCss(couleur);
  return {
    backgroundColor: `color-mix(in srgb, ${css} 24%, var(--surface))`,
    borderColor: `color-mix(in srgb, ${css} 70%, var(--border))`,
  };
}

/** La raison d'un echec, dite en clair plutot qu'en objet. */
function raisonDe(echec: unknown): string {
  return echec instanceof Error && echec.message !== ''
    ? echec.message
    : 'Enregistrement impossible';
}

/**
 * AUJOURD'HUI dans le fuseau de l'org (Parametres > Environnement), en date
 * locale a midi : la semaine ouverte et « Aujourd'hui » suivent l'org, la
 * grille des heures reste celle du poste (`docs/socle/fuseau-org.md`).
 */
function aujourdhuiDeLOrg(fuseau: string | undefined): Date {
  const [annee, mois, jour] = jourDansFuseau(new Date(), fuseau).split('-').map(Number);
  return new Date(annee ?? 1970, (mois ?? 1) - 1, jour ?? 1, 12);
}

/** Le dernier geste ecrit, et de quoi le defaire par le SDK. */
interface DernierGeste {
  texte: string;
  defaire: () => Promise<void>;
}

export function MaSemaine({ fiches, recherche }: ProprietesDeCanvas): JSX.Element {
  const { sdk, config } = useTakiApp();
  const { loading: authEnCours } = useUser();
  /* LE `personId` EST LE `workerId` D'UNE ENTREE (chantier `champ-personne`) :
     l'identifiant stable de la personne, celui de l'annuaire, jamais son
     adresse, qui change. Sans fiche membre (ou sur une fiche d'avant
     l'annuaire), la surface se rend en consultation plutot que d'ecrire
     n'importe quoi. */
  const personnesPort = usePersonnesPort();
  const selfId = personnesPort?.moi?.personId ?? '';

  /* L'ANNUAIRE DES PERSONNES, lu une fois par port : c'est lui qui donne un NOM
     a chaque agenda, et a chaque creneau d'un collegue. Illisible, la semaine
     tient quand meme : les agendas se disent « Compte inconnu », et on le DIT. */
  const [annuaire, setAnnuaire] = useState<readonly PersonneVue[]>([]);
  const [noteAnnuaire, setNoteAnnuaire] = useState<string | null>(null);
  useEffect(() => {
    if (personnesPort === null) {
      setAnnuaire([]);
      return;
    }
    let vivant = true;
    void personnesPort.lister().then(
      (liste) => {
        if (!vivant) return;
        setAnnuaire(liste);
        setNoteAnnuaire(null);
      },
      (echec: unknown) => {
        if (!vivant) return;
        setAnnuaire([]);
        setNoteAnnuaire(`Annuaire illisible : ${raisonDe(echec)}`);
      },
    );
    return () => {
      vivant = false;
    };
  }, [personnesPort]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const daysRef = useRef<HTMLDivElement>(null);
  const [hourHeight, setHourHeight] = useState(64);
  /* LA JOURNEE DE TRAVAIL EST VISIBLE A L'ARRIVEE. La grille commence a minuit,
     et personne ne pose un creneau a deux heures du matin : sans ce cadrage, la
     porte s'ouvrait sur sept heures de nuit vides et la journee sous le pli.
     UN SEUL defilement, a la premiere mesure ; ensuite la position appartient
     a l'utilisateur, et un redimensionnement ne la lui reprend pas. */
  const cadre = useRef(false);
  // Environ treize heures et demie visibles, avec un minimum lisible sur petit ecran.
  useEffect(() => {
    const viewport = scrollRef.current;
    const days = daysRef.current;
    if (viewport == null || days == null) return;
    const measure = (): void => {
      const hauteur = Math.max(48, (viewport.clientHeight - days.offsetHeight) / 13.5);
      setHourHeight(hauteur);
      if (!cadre.current) {
        cadre.current = true;
        viewport.scrollTo({ top: (HEURE_D_ARRIVEE - START_HOUR) * hauteur });
      }
    };
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(days);
    measure();
    return () => observer.disconnect();
  }, []);
  const gridHeight = (END_HOUR - START_HOUR) * hourHeight;

  /* ---------------------------------------------------------------- */
  /* Les fiches : celles de la porte, puis celles que le SDK a rendues */
  /* ---------------------------------------------------------------- */

  const [entrees, setEntrees] = useState<Fiche[]>(() => [...fiches]);
  useEffect(() => {
    setEntrees([...fiches]);
  }, [fiches]);

  const remplacer = useCallback((id: string, fiche: Fiche | null): void => {
    setEntrees((actuelles) => {
      if (fiche === null) return actuelles.filter((item) => item['id'] !== id);
      return actuelles.some((item) => item['id'] === id)
        ? actuelles.map((item) => (item['id'] === id ? fiche : item))
        : [...actuelles, fiche];
    });
  }, []);

  /** Le geste se voit AVANT la reponse du serveur ; l'echec remet l'etat d'avant. */
  const retoucher = useCallback((id: string, patch: Fiche): void => {
    setEntrees((actuelles) =>
      actuelles.map((item) => (item['id'] === id ? { ...item, ...patch } : item)),
    );
  }, []);

  /* LES CHOIX DU JEU viennent de la DECLARATION, jamais d'une liste recopiee
     ici : la fenetre « + Ajouter » propose exactement ce que la fiche du
     Journal propose, y compris quand l'org a renomme le champ ou regle sa
     propre liste dans le Studio (`catalogues.ts`). */
  const { instances } = useAppRuntime();
  const choixDuJeu = useMemo(
    () => champsAChoixDuJeu(instances, 'timesheets', 'journal'),
    [instances],
  );
  const clefsDeChoix = useMemo(() => choixDuJeu.map((champ) => champ.key), [choixDuJeu]);
  /* LE CHAMP QUI TITRE UNE SAISIE, lu dans la meme declaration. Sur le modele
     c'est la tache, un champ natif (14/10/2026) : `titreDuJeu` ne rend alors
     rien, et le creneau se titre par le nom de sa tache (`titreDe`). Une org
     qui titre encore par un champ a choix garde ce champ obligatoire. */
  const titre = useMemo(() => titreDuJeu(instances, 'timesheets', 'journal'), [instances]);
  /* LE NOM D'UNE TACHE dont le libelle est une liste (TAC Studio : une tache =
     un service, 14/10/2026) : le mot de l'option, jamais la valeur stockee. */
  const motsDesTaches = useMemo(
    () => champsAChoixDuJeu(instances, 'tasks', 'tache').find((champ) => champ.key === 'libelle')?.options ?? [],
    [instances],
  );
  /* L'ORDRE DES ETAPES D'UN PROJET (champ `etat`, tel que la declaration
     effective le porte) : il range le choix d'une tache. */
  const ordreDesEtats = useMemo(
    () => (champsAChoixDuJeu(instances, 'projects', 'projet').find((champ) => champ.key === 'etat')?.options ?? [])
      .map((option) => option.value),
    [instances],
  );
  /* ---------------------------------------------------------------- */
  /* Le catalogue des projets, et le nom de leur client                */
  /* ---------------------------------------------------------------- */

  const [projets, setProjets] = useState<readonly OptionDeProjet[]>([]);
  const [taches, setTaches] = useState<readonly OptionDeTache[]>([]);
  const [couleursDeProjets, setCouleursDeProjets] = useState<ReadonlyMap<string, Couleur>>(new Map());
  const [noteCatalogue, setNoteCatalogue] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        /* LES TACHES DES PROJETS (famille `tasks`, celles de Projets, jamais To
           do) : le choix « Projet / Tâche » les range sous leur projet.
           CHAQUE LECTURE EST INDEPENDANTE ET TYPEE : les regles refusent une
           lecture sans `type` a un Membre dont les droits sont donnes par
           type, et un refus ne doit pas vider les autres listes. Les refus
           qui comptent sont signales par une note. */
        const lu = await lireCatalogue(
          {
            projets: () => sdk.projects.list({ type: 'projet' }),
            contactsPersonnes: () => sdk.contacts.list({ type: 'person' }),
            contactsOrganisations: () => sdk.contacts.list({ type: 'organization' }),
            taches: () => sdk.tasks.list({ type: 'tache' }),
          },
          raisonDe,
        );
        const { projets: listeProjets, contacts: listeContacts, taches: listeTaches } = lu;
        if (!vivant) return;
        const clients = new Map(
          listeContacts.map((contact) => [contact.id, contactDisplayName(contact)]),
        );
        const options = listeProjets
          .map((projet) => {
            const brut = projet.customFields?.['libelle'];
            const nom = typeof brut === 'string' && brut.trim() !== '' ? brut.trim() : 'Projet';
            const client = projet.contactId == null ? undefined : clients.get(projet.contactId);
            /* DE QUOI RANGER LE CHOIX D'UNE TACHE (`comparerProjets`) : l'etape
               du projet, et sa date (son debut, sinon sa creation). */
            const etat = projet.customFields?.['etat'];
            const date = (projet.startAt ?? projet.createdAt)?.getTime();
            return {
              id: projet.id,
              label: client == null ? nom : `${client} · ${nom}`,
              ...(typeof etat === 'string' && etat !== '' ? { etat } : {}),
              ...(date !== undefined && Number.isFinite(date) ? { date } : {}),
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, 'fr-FR'));
        setProjets(options);
        /* La couleur de chaque projet qui en porte une : celle de ses creneaux. */
        setCouleursDeProjets(
          new Map(
            listeProjets.flatMap((projet) =>
              estCouleur(projet.couleur) ? [[projet.id, projet.couleur] as const] : [],
            ),
          ),
        );
        setTaches(
          listeTaches
            .flatMap((tache) => {
              const brut = tache.customFields?.['libelle'];
              const nom = typeof brut === 'string' && brut.trim() !== '' ? motDeLaValeur(motsDesTaches, brut.trim()) : '';
              /* La valeur stockee (`cm`) se cherche sous son libelle
                 (« Community management ») sans s'afficher. */
              return tache.projectId == null || nom === ''
                ? []
                : [{ id: tache.id, label: nom, projectId: tache.projectId, ...(typeof brut === 'string' ? { cle: brut.trim() } : {}) }];
            })
            .sort((a, b) => a.label.localeCompare(b.label, 'fr-FR')),
        );
        setNoteCatalogue(lu.notes.length === 0 ? null : lu.notes.join(' · '));
      } catch (echec: unknown) {
        if (!vivant) return;
        /* Le catalogue manquant ne casse pas la semaine : les creneaux perdent
           leur seconde ligne, et le selecteur de projet se vide. On le DIT. */
        setNoteCatalogue(`Projets illisibles : ${raisonDe(echec)}`);
      }
    })();
    return () => {
      vivant = false;
    };
  }, [sdk, config.orgId, motsDesTaches]);

  const etiquettes = useMemo(
    () => new Map(projets.map((projet) => [projet.id, projet.label])),
    [projets],
  );
  const nomsDeTaches = useMemo(() => new Map(taches.map((tache) => [tache.id, tache.label])), [taches]);
  /* Le titre d'une saisie est le nom de sa tache, comme dans le Journal
     (« Sans tâche » pour une ancienne saisie qui n'en porte pas). */
  const titreDe = (creneau: CreneauSemaine): string =>
    creneau.taskId == null ? 'Sans tâche' : nomsDeTaches.get(creneau.taskId) ?? 'Tâche indisponible';
  /** Le projet reste distinct du titre, qui designe toujours la tache. */
  const sousTitreDe = (creneau: CreneauSemaine): string => {
    if (creneau.projectId == null) return 'Sans projet';
    return etiquettes.get(creneau.projectId) ?? 'Projet indisponible';
  };

  /* ---------------------------------------------------------------- */
  /* La semaine affichee, et l'agenda regarde                          */
  /* ---------------------------------------------------------------- */

  const [week, setWeek] = useState(() => mondayOf(aujourdhuiDeLOrg(sdk.timezone)));
  const [weekendOverride, setWeekendOverride] = useState<boolean | null>(null);
  const [agendaChoisi, setAgendaChoisi] = useState<string | null>(null);
  const agenda = agendaChoisi ?? selfId;
  /* LE DROIT D'ECRIRE SUR LA PORTE (Timesheet, type `journal`), lu dans le port
     d'acces. Il ne fait que GRISER : la vraie barriere reste les rules. */
  const ecritureOuverte = peutEcrireSurPorte(useAccesPort(), 'timesheets', 'journal');
  const readOnly = selfId === '' || agenda !== selfId || !ecritureOuverte;

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('taki:lia:screen-context', {
      detail: {
        appId: 'timesheet', doorId: 'ma-semaine',
        period: { start: week.toISOString(), endExclusive: addDays(week, 7).toISOString() },
        personId: agenda || undefined,
      },
    }));
    return () => { window.dispatchEvent(new CustomEvent('taki:lia:screen-context', { detail: null })); };
  }, [week, agenda]);

  const [editor, setEditor] = useState<CreneauSemaine | 'new' | null>(null);
  /** L'endroit vise par un double-clic, lu seulement quand `editor` vaut `'new'`. */
  const [amorce, setAmorce] = useState<{ day: number; debutMinutes: number } | null>(null);
  const [geste, setGeste] = useState<DernierGeste | null>(null);
  const [enEcriture, setEnEcriture] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{
    payload: DragPayload;
    duration: number;
    grabOffset: number;
  } | null>(null);
  const [dragPreview, setDragPreview] = useState<{
    day: number;
    start: number;
    duration: number;
  } | null>(null);
  const resizeRef = useRef<{
    creneau: CreneauSemaine;
    y: number;
    scroll: number;
    duration: number;
  } | null>(null);
  const [resizePreview, setResizePreview] = useState<{ id: string; duration: number } | null>(null);
  /** Le menu du clic droit : le creneau vise et le point ou il s'ouvre (ecran). */
  const [menu, setMenu] = useState<{ creneau: CreneauSemaine; x: number; y: number } | null>(null);
  const [placeDuMenu, setPlaceDuMenu] = useState<{ left: number; top: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /** L'article qui a ouvert le menu : c'est lui qui retrouve le focus a la fermeture. */
  const declencheurRef = useRef<HTMLElement | null>(null);

  const google = useGoogleAgenda(week);

  const creneaux = useMemo(() => creneauxDeLaSemaine(entrees, week, titre), [entrees, week, titre]);
  const deLAgenda = creneaux.filter((creneau) => creneau.ownerId === agenda);

  /** Le NOM d'une personne par son `personId`, lu dans l'annuaire. */
  const noms = useMemo(
    () => new Map(annuaire.map((personne) => [personne.personId, titreDePersonne(personne)])),
    [annuaire],
  );
  /**
   * LE MOT D'UN AGENDA. Un identifiant de personne ne s'affiche jamais : une
   * personne absente de l'annuaire se dit « Compte inconnu ». Seule exception,
   * une entree d'avant l'annuaire, dont le `workerId` est encore une ADRESSE
   * (tant que le rattrapage n'est pas passe) : l'adresse se lit, on la montre.
   */
  const nomDe = useCallback(
    (id: string): string => noms.get(id) ?? (id.includes('@') ? id : REPLI_PERSONNE_INCONNUE),
    [noms],
  );

  /**
   * LES PERSONNES DONT ON PEUT REGARDER L'AGENDA, par NOM : celles de
   * l'annuaire qui sont encore la, plus toute personne qui a une entree dans le
   * jeu (une personne retiree garde son agenda du passe), la personne
   * connectee toujours en tete.
   */
  const personnes = useMemo<[string, string][]>(() => {
    const ids = new Set<string>();
    for (const personne of annuaire) {
      if (!personne.archive) ids.add(personne.personId);
    }
    for (const fiche of entrees) {
      const id = personneDeFiche(fiche);
      if (id !== '') ids.add(id);
    }
    ids.delete(selfId);
    const liste: [string, string][] = [...ids]
      .map((id): [string, string] => [id, nomDe(id)])
      .sort((a, b) => a[1].localeCompare(b[1], 'fr-FR'));
    if (selfId !== '') liste.unshift([selfId, nomDe(selfId)]);
    return liste;
  }, [annuaire, entrees, selfId, nomDe]);

  // La recherche ne masque jamais la presence de travail pendant le week-end.
  const weekendHours = deLAgenda
    .filter((creneau) => creneau.day >= 5)
    .reduce((somme, creneau) => somme + creneau.duration, 0);
  const showWeekend = weekendOverride ?? weekendHours > 0;
  const days = showWeekend ? JOURS : JOURS.slice(0, 5);
  const columns = showWeekend ? FULL_WEEK_COLUMNS : WEEKDAY_COLUMNS;

  const term = recherche.trim().toLocaleLowerCase('fr-FR');
  const visibles = deLAgenda.filter((creneau) => {
    if (term === '') return true;
    return `${titreDe(creneau)} ${sousTitreDe(creneau)}`.toLocaleLowerCase('fr-FR').includes(term);
  });

  // Les evenements Google sont ceux de la personne connectee : ils vivent sur
  // « Mon agenda » et s'effacent sur l'agenda d'un collegue. La recherche les
  // filtre sur leur titre, comme les creneaux.
  const googleEvents = readOnly
    ? []
    : google.events.filter(
        (event) => term === '' || event.title.toLocaleLowerCase('fr-FR').includes(term),
      );
  const googleAllDay = readOnly
    ? []
    : google.allDay.filter(
        (event) => term === '' || event.title.toLocaleLowerCase('fr-FR').includes(term),
      );
  // Un creneau Taki et un evenement Google qui se chevauchent se partagent la
  // colonne, creneau a gauche, evenement a droite : aucun des deux ne cache l'autre.
  const sharesWithGoogle = (creneau: CreneauSemaine): boolean =>
    googleEvents.some((event) => event.day === creneau.day && overlaps(event, creneau));
  const sharesWithBlock = (event: { day: number; start: number; duration: number }): boolean =>
    visibles.some((creneau) => creneau.day === event.day && overlaps(event, creneau));

  /* ---------------------------------------------------------------- */
  /* Ecrire : une seule porte, et un seul niveau d'annulation          */
  /* ---------------------------------------------------------------- */

  const ecrire = useCallback(
    async (
      texte: string,
      action: () => Promise<void>,
      repli: () => void,
      defaire: () => Promise<void>,
    ): Promise<void> => {
      setEnEcriture(true);
      setErreur(null);
      try {
        await action();
        setGeste({ texte, defaire });
      } catch (echec: unknown) {
        repli();
        setGeste(null);
        setErreur(raisonDe(echec));
      } finally {
        setEnEcriture(false);
      }
    },
    [],
  );

  const majFiche = async (id: string, patch: TimesheetUpdateInput): Promise<void> => {
    const ecrite = await sdk.timesheets.update(id, patch);
    remplacer(id, { ...ecrite });
  };

  const ficheDe = (id: string): Fiche | undefined => entrees.find((item) => item['id'] === id);

  /** Deplacer un creneau : seul son instant de debut change. */
  const deplacer = (creneau: CreneauSemaine, day: number, start: number): void => {
    const avantFiche = ficheDe(creneau.id);
    if (avantFiche === undefined) return;
    const avant = photoDeFiche(avantFiche);
    const startAt = instantDuCreneau(week, day, start);
    retoucher(creneau.id, { startAt });
    void ecrire(
      `Créneau déplacé ${JOURS_LONGS[day] ?? ''} à ${timeLabel(start)}`,
      () => majFiche(creneau.id, { startAt }),
      () => remplacer(creneau.id, avantFiche),
      () => majFiche(creneau.id, avant),
    );
  };

  /** Redimensionner : seule la duree change, calee sur le pas de cinq minutes. */
  const redimensionner = (creneau: CreneauSemaine, duration: number): void => {
    const avantFiche = ficheDe(creneau.id);
    if (avantFiche === undefined) return;
    const avant = photoDeFiche(avantFiche);
    const durationMinutes = minutesDeDuree(duration);
    retoucher(creneau.id, { durationMinutes });
    void ecrire(
      'Durée modifiée',
      () => majFiche(creneau.id, { durationMinutes }),
      () => remplacer(creneau.id, avantFiche),
      () => majFiche(creneau.id, avant),
    );
  };

  /**
   * Supprimer : une ARCHIVE, jamais un delete dur, donc « Annuler » la rend.
   * Le texte du pied dit d'ou vient le geste : « supprime » pour le bouton de
   * l'editeur, « archive » pour le menu du clic droit.
   */
  const supprimer = (creneau: CreneauSemaine, texte = 'Créneau supprimé'): void => {
    const avantFiche = ficheDe(creneau.id);
    if (avantFiche === undefined) return;
    setEditor(null);
    remplacer(creneau.id, null);
    void ecrire(
      texte,
      async () => {
        await sdk.timesheets.archive(creneau.id);
      },
      () => remplacer(creneau.id, avantFiche),
      async () => {
        const rendue = await sdk.timesheets.unarchive(creneau.id);
        remplacer(creneau.id, { ...rendue });
      },
    );
  };

  /** Archiver, depuis le menu du clic droit : l'archivage habituel, annulable. */
  const archiver = (creneau: CreneauSemaine): void => supprimer(creneau, 'Créneau archivé');

  /**
   * Dupliquer : une fiche neuve, identique a l'originale, un quart d'heure plus
   * tard (`copieDeCreneau`). Elle apparait des que le SDK la rend, en
   * superposition. Une copie qui passe minuit reste ecrite : la grille suit la
   * semaine, aucun cas special. « Annuler » archive la copie.
   */
  const dupliquer = (creneau: CreneauSemaine): void => {
    if (readOnly || enEcriture) return;
    const source = ficheDe(creneau.id);
    if (source === undefined) return;
    const entree = copieDeCreneau(source, config.orgId, DECALAGE_DE_COPIE);
    if (entree === null) return;
    let nouvelId = '';
    void ecrire(
      'Créneau dupliqué',
      async () => {
        const creee = await sdk.timesheets.create(entree);
        nouvelId = creee.id;
        remplacer(creee.id, { ...creee });
      },
      () => undefined,
      async () => {
        if (nouvelId === '') return;
        await sdk.timesheets.archive(nouvelId);
        remplacer(nouvelId, null);
      },
    );
  };

  /* ---------------------------------------------------------------- */
  /* Le menu du clic droit sur un creneau                              */
  /* ---------------------------------------------------------------- */

  /**
   * Ouvrir le menu A LA PLACE de celui du navigateur, y compris en lecture
   * seule (choix grises) : le menu du navigateur ne revient jamais sur un
   * creneau. Au clavier (touche Menu, Maj+F10) les coordonnees valent 0 : le
   * menu s'ancre alors au coin bas-gauche du creneau.
   */
  const ouvrirMenu = (event: MouseEvent<HTMLElement>, creneau: CreneauSemaine): void => {
    event.preventDefault();
    event.stopPropagation();
    const article = event.currentTarget;
    declencheurRef.current = article;
    if (event.clientX === 0 && event.clientY === 0) {
      const cadreDeLArticle = article.getBoundingClientRect();
      setMenu({ creneau, x: cadreDeLArticle.left, y: cadreDeLArticle.bottom });
    } else {
      setMenu({ creneau, x: event.clientX, y: event.clientY });
    }
  };
  const fermerMenu = (): void => setMenu(null);
  const rendreLeFocus = (): void => declencheurRef.current?.focus({ preventScroll: true });

  /* LA PLACE SE MESURE APRES LE MONTAGE, avant la peinture : le menu garde
     l'ecran en entier, a gauche ou au-dessus du point s'il deborderait. Le
     focus va a la premiere entree. */
  useLayoutEffect(() => {
    const noeud = menuRef.current;
    if (menu == null || noeud == null) {
      setPlaceDuMenu(null);
      return;
    }
    const cadreDuMenu = noeud.getBoundingClientRect();
    setPlaceDuMenu(
      positionDuMenu(
        menu.x,
        menu.y,
        { largeur: cadreDuMenu.width, hauteur: cadreDuMenu.height },
        { largeur: window.innerWidth, hauteur: window.innerHeight },
      ),
    );
    noeud.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
  }, [menu]);

  /* UN CLIC AILLEURS FERME LE MENU (phase de capture : meme si la cible arrete
     la propagation). Le defilement, un autre agenda ou une autre semaine aussi. */
  useEffect(() => {
    if (menu == null) return;
    const dehors = (event: PointerEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) setMenu(null);
    };
    document.addEventListener('pointerdown', dehors, true);
    return () => document.removeEventListener('pointerdown', dehors, true);
  }, [menu]);
  useEffect(() => {
    setMenu(null);
  }, [week, agenda]);

  const clavierDuMenu = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      fermerMenu();
      rendreLeFocus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const entrees = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]')];
    const index = entrees.indexOf(document.activeElement as HTMLElement);
    const suivant =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? entrees.length - 1
          : (index + (event.key === 'ArrowDown' ? 1 : -1) + entrees.length) % entrees.length;
    entrees[suivant]?.focus({ preventScroll: true });
  };

  /** Enregistrer la saisie de l'editeur : une creation, ou une modification. */
  const enregistrer = (saisie: SaisieDeCreneau): void => {
    if (readOnly || enEcriture) return;
    setEditor(null);
    const startAt = instantDuJour(saisie.jour, saisie.debutMinutes);
    const durationMinutes = calerMinutes(saisie.finMinutes - saisie.debutMinutes);

    if (saisie.id === null) {
      const entree: TimesheetCreateInput = {
        orgId: config.orgId,
        type: 'journal',
        workerId: selfId,
        startAt,
        durationMinutes,
        ...(saisie.projectId === '' ? {} : { projectId: saisie.projectId }),
        ...(saisie.taskId === '' ? {} : { taskId: saisie.taskId }),
        customFields: {
          ...(saisie.notes === '' ? {} : { notes: saisie.notes }),
          ...Object.fromEntries(
            Object.entries(saisie.choix).filter(([, valeur]) => valeur !== ''),
          ),
        },
      };
      /* L'id n'existe qu'apres l'ecriture : `ecrire` ne retient `defaire`
         qu'une fois l'action reussie, donc la fermeture lit bien le vrai id. */
      let nouvelId = '';
      void ecrire(
        'Créneau ajouté',
        async () => {
          const creee = await sdk.timesheets.create(entree);
          nouvelId = creee.id;
          remplacer(creee.id, { ...creee });
        },
        () => undefined,
        async () => {
          if (nouvelId === '') return;
          await sdk.timesheets.archive(nouvelId);
          remplacer(nouvelId, null);
        },
      );
    } else {
      const id = saisie.id;
      const avantFiche = ficheDe(id);
      if (avantFiche === undefined) return;
      /* L'ANNULATION CITE LES DEUX CLEFS QUE CE PATCH ECRIT : sans elles, une
         note posee a l'instant survivrait a « Annuler » (le sac fusionne clef
         par clef, il ne se remplace pas). */
      const avant = photoAvecClefs(avantFiche, ['notes', ...clefsDeChoix]);
      /* LE SAC SE FUSIONNE CLEF PAR CLEF cote SDK : `notes` et les choix
         suffisent, les autres clefs du document restent en place (un ancien
         libelle y reste, sans s'afficher). Le projet suit toujours la tache
         choisie dans l'editeur. */
      const patch: TimesheetUpdateInput = {
        startAt,
        durationMinutes,
        projectId: saisie.projectId === '' ? null : saisie.projectId,
        taskId: saisie.taskId === '' ? null : saisie.taskId,
        customFields: {
          notes: saisie.notes,
          ...Object.fromEntries(clefsDeChoix.map((clef) => [clef, saisie.choix[clef] ?? ''])),
        },
      };
      retoucher(id, {
        startAt,
        durationMinutes,
        projectId: saisie.projectId === '' ? undefined : saisie.projectId,
        taskId: saisie.taskId === '' ? undefined : saisie.taskId,
        customFields: {
          ...(typeof avantFiche['customFields'] === 'object' && avantFiche['customFields'] !== null
            ? (avantFiche['customFields'] as Record<string, unknown>)
            : {}),
          notes: saisie.notes,
          ...Object.fromEntries(clefsDeChoix.map((clef) => [clef, saisie.choix[clef] ?? ''])),
        },
      });
      void ecrire(
        'Créneau modifié',
        () => majFiche(id, patch),
        () => remplacer(id, avantFiche),
        () => majFiche(id, avant),
      );
    }

    // La semaine suit le creneau, le week-end s'ouvre s'il en porte un, et la
    // grille se cale sur l'heure posee : on voit ce qu'on vient d'ecrire.
    const cible = mondayOf(startAt);
    if (weekKey(cible) !== weekKey(week)) setWeek(cible);
    const jour = (startAt.getDay() + 6) % 7;
    setWeekendOverride(jour >= 5 ? true : null);
    scrollRef.current?.scrollTo({
      top: Math.max(0, (saisie.debutMinutes / 60 - 1) * hourHeight),
      behavior: 'smooth',
    });
  };

  /** Defaire le dernier geste, par le SDK. Un seul niveau, comme avant. */
  const annuler = (): void => {
    const courant = geste;
    if (courant == null || enEcriture) return;
    setGeste(null);
    setEnEcriture(true);
    setErreur(null);
    void courant
      .defaire()
      .catch((echec: unknown) => {
        setErreur(raisonDe(echec));
        setGeste(courant);
      })
      .finally(() => {
        setEnEcriture(false);
      });
  };

  /* ---------------------------------------------------------------- */
  /* Le glisser-deposer, au pas de cinq minutes                        */
  /* ---------------------------------------------------------------- */

  const dragStart = (event: DragEvent, payload: DragPayload): void => {
    if (readOnly || enEcriture) {
      event.preventDefault();
      return;
    }
    const creneau = visibles.find((item) => item.id === payload.blockId);
    if (creneau == null) {
      event.preventDefault();
      return;
    }
    // Conserver le point saisi, y compris les 3 px entre l'heure et le bord visuel.
    const grabOffset = (event.clientY - event.currentTarget.getBoundingClientRect().top + 3) / hourHeight;
    event.dataTransfer.effectAllowed = 'move';
    const serialized = JSON.stringify(payload);
    event.dataTransfer.setData('application/x-taki-planner', serialized);
    /* Repli WebKit et outils d'automatisation qui ne transportent que text/plain. */
    event.dataTransfer.setData('text/plain', serialized);
    setDragging({ payload, duration: creneau.duration, grabOffset });
  };
  const dropStart = (event: DragEvent<HTMLDivElement>, duration: number, grabOffset: number): number => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const raw = START_HOUR + (event.clientY - bounds.top) / hourHeight - grabOffset;
    return Math.max(START_HOUR, Math.min(END_HOUR - duration, snap(raw)));
  };
  const dragOver = (event: DragEvent<HTMLDivElement>, day: number): void => {
    if (readOnly || dragging == null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragPreview({
      day,
      start: dropStart(event, dragging.duration, dragging.grabOffset),
      duration: dragging.duration,
    });
  };
  const drop = (event: DragEvent<HTMLDivElement>, day: number): void => {
    if (readOnly || dragging == null) return;
    event.preventDefault();
    setDragPreview(null);
    setDragging(null);
    let payload: DragPayload;
    try {
      payload = JSON.parse(
        event.dataTransfer.getData('application/x-taki-planner') ||
          event.dataTransfer.getData('text/plain'),
      ) as DragPayload;
    } catch {
      return;
    }
    if (payload.kind !== 'block' || payload.blockId !== dragging.payload.blockId) return;
    const creneau = visibles.find((item) => item.id === payload.blockId);
    if (creneau == null) return;
    deplacer(creneau, day, dropStart(event, creneau.duration, dragging.grabOffset));
  };

  const totalForDay = (day: number): number =>
    visibles.filter((creneau) => creneau.day === day).reduce((somme, item) => somme + item.duration, 0);

  const changeWeek = (next: Date): void => {
    setGeste(null);
    setErreur(null);
    setWeek(next);
    setWeekendOverride(null);
  };

  /**
   * Le double-clic sur le FOND d'une colonne : un creneau, un evenement Google
   * ou une poignee gardent leurs propres gestes. En consultation, rien.
   */
  const viseLeFond = (event: MouseEvent<HTMLDivElement>): boolean =>
    !(event.target instanceof Element && event.target.closest('article, a, button') != null);
  const poserAuDoubleClic = (event: MouseEvent<HTMLDivElement>, day: number): void => {
    if (readOnly || dragging != null || resizeRef.current != null || !viseLeFond(event)) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const minutes = (START_HOUR + (event.clientY - bounds.top) / hourHeight) * 60;
    const debutMinutes = Math.max(
      0,
      Math.min(1440 - QUART_D_HEURE, Math.floor(minutes / QUART_D_HEURE) * QUART_D_HEURE),
    );
    setAmorce({ day, debutMinutes });
    setEditor('new');
  };

  /** Le jour propose a l'ouverture de l'editeur, date pure `YYYY-MM-DD`. */
  const jourInitial = (cible: CreneauSemaine | 'new'): string => {
    if (cible !== 'new') return weekKey(addDays(week, cible.day));
    if (amorce != null) return weekKey(addDays(week, amorce.day));
    const aujourdhui = aujourdhuiDeLOrg(sdk.timezone);
    // Sur une autre semaine que celle d'aujourd'hui, le lundi affiche : un
    // ajout ne saute pas dans le temps sans qu'on l'ait demande.
    return weekKey(weekKey(mondayOf(aujourdhui)) === weekKey(week) ? aujourdhui : week);
  };

  const raisonLecture =
    selfId === ''
      ? "Aucune fiche membre : impossible d'écrire dans le journal"
      : !ecritureOuverte
        ? 'Journal en lecture seule pour votre compte'
        : "L'agenda d'un collègue est en consultation";

  /* LA CASCADE : un creneau qui en recouvre un autre du meme jour se decale
     d'un cran a droite (quatre crans au plus) et passe devant. Profondeur 0 :
     le creneau reste exactement ou il etait. */
  const profondeurs = profondeursDeCascade(visibles);

  return (
    <div className="taki-anim-fade flex h-full min-h-0 flex-col overflow-hidden bg-surface">
      {/* ================================================================ */}
      {/* La barre de la semaine. Le « Ajouter » est A GAUCHE de tout, la   */}
      {/* ou le geste commence : c'est la rangee 3a que cette porte n'a pas.*/}
      {/* ================================================================ */}
      <header className="flex min-h-14 shrink-0 flex-wrap items-center gap-2 border-b border-border-soft px-4 py-2">
        {/* LE MEME HABIT QUE LE « + Ajouter » DU GABARIT (regle G5) : variante
            primaire, taille `sm`, IconPlus a 15, libelle universel « Ajouter ». */}
        <Button
          variant="primary"
          size="sm"
          aria-label="Ajouter"
          aria-disabled={readOnly}
          title={readOnly ? raisonLecture : 'Ajouter un créneau · ou double-clic sur la grille'}
          onClick={() => {
            if (readOnly) return;
            setAmorce(null);
            setEditor('new');
          }}
        >
          <IconPlus size={15} className="shrink-0" />
          Ajouter
        </Button>
        {/* Le filet separe le geste de creation de la navigation dans le temps. */}
        <span aria-hidden className="mx-2 h-4 w-px shrink-0 bg-border" />
        <Button
          variant="secondary"
          size="sm"
          aria-label="Semaine précédente"
          title="Semaine précédente"
          onClick={() => changeWeek(addDays(week, -7))}
          className="w-8 px-0 focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
        >
          <IconChevronRight size={14} className="rotate-180" />
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => changeWeek(mondayOf(aujourdhuiDeLOrg(sdk.timezone)))}
          className="focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
        >
          Aujourd&apos;hui
        </Button>
        <Button
          variant="secondary"
          size="sm"
          aria-label="Semaine suivante"
          title="Semaine suivante"
          onClick={() => changeWeek(addDays(week, 7))}
          className="w-8 px-0 focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
        >
          <IconChevronRight size={14} />
        </Button>
        <h2 aria-live="polite" className="ml-1 text-sm font-semibold text-ink-900">
          {weekLabel(week, showWeekend)}
        </h2>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <GoogleAgendaButton agenda={google} />
          <AgendaPicker
            people={personnes}
            value={agenda}
            selfId={selfId}
            onChange={(id) => {
              setAgendaChoisi(id);
              setGeste(null);
              setErreur(null);
              setWeekendOverride(null);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            aria-label={showWeekend ? 'Masquer le week-end' : 'Afficher le week-end'}
            aria-pressed={showWeekend}
            title={showWeekend ? 'Replier samedi et dimanche' : 'Afficher samedi et dimanche'}
            onClick={() => setWeekendOverride(!showWeekend)}
          >
            {showWeekend ? '−2 j' : '+2 j'}
            {!showWeekend && weekendHours > 0 ? ` · ${hoursLabel(weekendHours)}` : ''}
          </Button>
          <ViewSelector
            activeViewId="timesheet.calendar"
            views={[
              {
                id: 'timesheet.calendar',
                label: 'Calendrier',
                icon: <IconCalendarMark size={16} />,
                level: 'collection',
                disabled: false,
              },
              {
                id: 'timesheet.list',
                label: 'Liste',
                icon: <IconBulletList size={16} />,
                level: 'collection',
                disabled: true,
                reason: 'Le journal complet est dans la porte Journal',
              },
            ]}
          />
        </div>
      </header>

      {/* ================================================================ */}
      {/* La semaine, de minuit a minuit, sur toute la largeur.             */}
      {/* ================================================================ */}
      <div
        ref={scrollRef}
        onScroll={() => {
          if (menu != null) setMenu(null);
        }}
        tabIndex={0}
        role="region"
        aria-label="Calendrier de la semaine, de minuit à minuit"
        className="min-h-0 flex-1 overflow-auto overscroll-contain focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
        style={{ scrollbarGutter: 'stable' }}
      >
        <div className="min-w-[42.5rem]">
          <div ref={daysRef} className="sticky top-0 z-20 border-b border-border-soft bg-surface">
            <div className={cn('grid', columns)}>
              <div />
              {days.map((name, day) => {
                const date = addDays(week, day);
                return (
                  <div key={name} className="flex h-9 items-center gap-2 border-l border-border-soft px-3">
                    <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                      <span className="text-xs font-medium text-ink-500">{name}</span>
                      <span className="text-sm font-semibold text-ink-900">{date.getDate()}</span>
                    </div>
                    <span className="ml-auto text-xs tabular-nums text-ink-400">
                      {hoursLabel(totalForDay(day))}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Les journees entieres Google, sous les jours : une fois connecte, sur Mon agenda, et seulement s'il y en a. */}
            {google.connected && !readOnly && (
              <GoogleAllDayRow events={googleAllDay} dayCount={days.length} columns={columns} />
            )}
          </div>
          <div className={cn('grid', columns)}>
            <div className="relative bg-surface" style={{ height: `${gridHeight}px` }}>
              {HOURS.slice(0, -1).map((hour, index) => (
                <span
                  key={hour}
                  className={cn(
                    'absolute right-2 bg-surface px-1 text-[0.6875rem] tabular-nums text-ink-400',
                    index === 0 ? 'top-1' : '-translate-y-1/2',
                  )}
                  style={{ top: index === 0 ? undefined : `${(hour - START_HOUR) * hourHeight}px` }}
                >
                  {timeLabel(hour)}
                </span>
              ))}
            </div>
            {days.map((name, day) => (
              <div
                key={name}
                className="relative border-l border-border-soft bg-surface"
                style={{ height: `${gridHeight}px` }}
                onDragOver={(event) => dragOver(event, day)}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragPreview(null);
                }}
                onDrop={(event) => drop(event, day)}
                onMouseDown={(event) => {
                  // Le second clic d'un double-clic ne selectionne pas les heures.
                  if (event.detail > 1 && !readOnly && viseLeFond(event)) event.preventDefault();
                }}
                onDoubleClick={(event) => poserAuDoubleClic(event, day)}
              >
                {HOURS.slice(0, -1).map((hour) => (
                  <div
                    key={hour}
                    aria-hidden
                    className="absolute inset-x-0 border-t border-border-soft"
                    style={{ top: `${(hour - START_HOUR) * hourHeight}px`, height: `${hourHeight}px` }}
                  >
                    <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-border-soft opacity-60" />
                  </div>
                ))}
                {dragPreview?.day === day && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-1.5 z-20 rounded-sm border border-dashed border-ink-400 bg-surface-1/80"
                    style={{
                      top: `${(dragPreview.start - START_HOUR) * hourHeight + 3}px`,
                      height: `${Math.max(28, dragPreview.duration * hourHeight - 6)}px`,
                    }}
                  />
                )}
                {visibles
                  .filter((creneau) => creneau.day === day)
                  .map((original) => {
                    const creneau =
                      resizePreview?.id === original.id
                        ? { ...original, duration: resizePreview.duration }
                        : original;
                    const sousTitre = sousTitreDe(creneau);
                    const nomTache = titreDe(creneau);
                    const hauteur = Math.max(28, creneau.duration * hourHeight - 6);
                    const profondeur = profondeurs.get(original.id) ?? 0;
                    return (
                      <article
                        key={creneau.id}
                        role="button"
                        tabIndex={0}
                        aria-label={`${nomTache}, ${sousTitre}, ${timeLabel(creneau.start)} à ${timeLabel(creneau.start + creneau.duration)}`}
                        onClick={() => setEditor(original)}
                        onContextMenu={(event) => ouvrirMenu(event, original)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setEditor(original);
                          }
                        }}
                        draggable={!readOnly && !enEcriture && resizePreview == null}
                        onDragStart={(event) => dragStart(event, { kind: 'block', blockId: creneau.id })}
                        onDragEnd={() => {
                          setDragging(null);
                          setDragPreview(null);
                        }}
                        title={`${nomTache} · ${sousTitre} · ${nomDe(creneau.ownerId)} · ${timeLabel(creneau.start)} - ${timeLabel(creneau.start + creneau.duration)}`}
                        className={cn(
                          'absolute right-1.5 cursor-grab overflow-hidden rounded-sm border px-2 py-0.5 transition-[filter] hover:brightness-[0.98]',
                          profondeur > 0 && 'ring-1 ring-surface',
                        )}
                        style={{
                          ...tonDuCreneau(couleurDuCreneau(creneau.projectId, creneau.id, couleursDeProjets)),
                          left: `calc(0.375rem + ${Math.min(profondeur, 4)} * 0.75rem)`,
                          zIndex: 10 + Math.min(profondeur, 9),
                          top: `${(creneau.start - START_HOUR) * hourHeight + 3}px`,
                          height: `${hauteur}px`,
                          ...(sharesWithGoogle(creneau) ? { right: 'calc(40% + 0.1875rem)' } : {}),
                        }}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="min-w-0 flex-1 truncate text-xs font-semibold leading-4 text-ink-900">{nomTache}</p>
                          {hauteur < 66 && <span className="shrink-0 text-[0.625rem] tabular-nums leading-4 text-ink-700">
                            {timeLabel(creneau.start)} - {timeLabel(creneau.start + creneau.duration)}
                          </span>}
                        </div>
                        {hauteur >= 42 && <p className="mt-0.5 truncate text-[0.625rem] leading-[14px] text-ink-700">{sousTitre}</p>}
                        {hauteur >= 66 && <p className="mt-1 text-[0.6875rem] tabular-nums text-ink-700">
                          {timeLabel(creneau.start)} - {timeLabel(creneau.start + creneau.duration)}
                        </p>}
                        {!readOnly && (
                          <button
                            type="button"
                            aria-label={`Redimensionner ${nomTache}`}
                            title="Glisser pour ajuster la fin · pas de 5 min"
                            className="absolute inset-x-0 bottom-0 flex h-2 cursor-ns-resize touch-none items-center justify-center bg-border-soft/50"
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            onDragStart={(event) => event.preventDefault()}
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              if (enEcriture) return;
                              event.currentTarget.setPointerCapture(event.pointerId);
                              resizeRef.current = {
                                creneau: original,
                                y: event.clientY,
                                scroll: scrollRef.current?.scrollTop ?? 0,
                                duration: original.duration,
                              };
                              setResizePreview({ id: creneau.id, duration: creneau.duration });
                            }}
                            onPointerMove={(event) => {
                              const resize = resizeRef.current;
                              if (resize == null) return;
                              const delta =
                                event.clientY - resize.y + (scrollRef.current?.scrollTop ?? 0) - resize.scroll;
                              resize.duration = Math.max(
                                STEP,
                                Math.min(24 - resize.creneau.start, snap(resize.creneau.duration + delta / hourHeight)),
                              );
                              setResizePreview({ id: resize.creneau.id, duration: resize.duration });
                            }}
                            onPointerUp={(event) => {
                              event.stopPropagation();
                              const resize = resizeRef.current;
                              if (resize == null) return;
                              resizeRef.current = null;
                              setResizePreview(null);
                              redimensionner(resize.creneau, resize.duration);
                            }}
                            onPointerCancel={() => {
                              resizeRef.current = null;
                              setResizePreview(null);
                            }}
                            onKeyDown={(event) => {
                              event.stopPropagation();
                              if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
                              event.preventDefault();
                              if (enEcriture) return;
                              const duration = Math.max(
                                STEP,
                                Math.min(
                                  24 - creneau.start,
                                  snap(creneau.duration + (event.key === 'ArrowUp' ? -STEP : STEP)),
                                ),
                              );
                              redimensionner(original, duration);
                            }}
                          >
                            <span className="h-px w-5 bg-ink-500" />
                          </button>
                        )}
                      </article>
                    );
                  })}
                {googleEvents
                  .filter((event) => event.day === day)
                  .map((event) => (
                    <GoogleEventCard
                      key={`google-${event.id}`}
                      event={event}
                      style={{
                        top: `${(event.start - START_HOUR) * hourHeight + 3}px`,
                        height: `${Math.max(28, event.duration * hourHeight - 6)}px`,
                        ...(sharesWithBlock(event) ? { left: 'calc(60% + 0.1875rem)' } : {}),
                      }}
                    />
                  ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {menu != null && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={`Gestes sur ${titreDe(menu.creneau)}`}
          onKeyDown={clavierDuMenu}
          onBlur={(event) => {
            if (fermeAuBlur(event.currentTarget, event.relatedTarget)) fermerMenu();
          }}
          onContextMenu={(event) => event.preventDefault()}
          className="fixed z-50 flex min-w-48 flex-col rounded-md border border-border bg-surface p-1 shadow-pop"
          style={{ left: `${placeDuMenu?.left ?? menu.x}px`, top: `${placeDuMenu?.top ?? menu.y}px` }}
        >
          {[
            { id: 'dupliquer', libelle: 'Dupliquer', agir: dupliquer },
            { id: 'archiver', libelle: 'Archiver', agir: archiver },
          ].map((entree) => {
            const eteinte = readOnly || enEcriture;
            return (
              <button
                key={entree.id}
                type="button"
                role="menuitem"
                aria-disabled={eteinte || undefined}
                title={eteinte ? (readOnly ? raisonLecture : 'Enregistrement en cours') : undefined}
                onClick={() => {
                  if (eteinte) return;
                  const cible = menu.creneau;
                  fermerMenu();
                  rendreLeFocus();
                  entree.agir(cible);
                }}
                className={cn(
                  'flex h-8 items-center rounded-sm px-2 text-left font-sans text-[0.8125rem] font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                  eteinte ? 'cursor-default text-ink-300' : 'text-ink-900 hover:bg-surface-1',
                )}
              >
                {entree.libelle}
              </button>
            );
          })}
        </div>
      )}

      {editor != null && (
        <EventEditor
          key={editor === 'new' ? 'new' : editor.id}
          creneau={editor === 'new' ? null : editor}
          jourInitial={jourInitial(editor)}
          debutPropose={editor === 'new' ? (amorce?.debutMinutes ?? null) : null}
          projets={projets}
          taches={taches}
          ordreDesEtats={ordreDesEtats}
          clefDuTitre={titre?.key ?? null}
          choixDuJeu={choixDuJeu}
          readOnly={readOnly}
          occupe={enEcriture}
          onClose={() => setEditor(null)}
          onSave={enregistrer}
          onDelete={editor === 'new' || readOnly ? null : () => supprimer(editor)}
        />
      )}

      {/* ================================================================ */}
      {/* Le pied : l'etat REEL de l'enregistrement, puis le dernier geste. */}
      {/* ================================================================ */}
      <footer className="flex min-h-9 shrink-0 items-center gap-3 border-t border-border-soft px-4 text-xs text-ink-400">
        {erreur != null ? (
          <span role="alert" className="text-danger">
            {erreur}
          </span>
        ) : enEcriture ? (
          <span role="status">Enregistrement…</span>
        ) : authEnCours ? (
          <span>Connexion en cours</span>
        ) : selfId === '' ? (
          <span>Aucune fiche membre : le journal est en consultation</span>
        ) : geste != null ? (
          <span>Enregistré</span>
        ) : null}
        {noteCatalogue != null && <span>{noteCatalogue}</span>}
        {noteAnnuaire != null && <span>{noteAnnuaire}</span>}
        {google.error != null ? (
          <span role="alert" className="text-danger">
            {google.error}
          </span>
        ) : (
          google.connected && (
            <span>
              {readOnly
                ? 'Google Agenda visible sur Mon agenda'
                : google.busy
                  ? 'Lecture de Google Agenda…'
                  : `${google.events.length + google.allDay.length} événement${google.events.length + google.allDay.length > 1 ? 's' : ''} Google cette semaine`}
            </span>
          )
        )}
        {geste != null && (
          <span role="status" className="ml-auto flex items-center gap-2 text-ink-500">
            {geste.texte}
            <button
              type="button"
              aria-disabled={enEcriture}
              onClick={annuler}
              className="h-7 rounded-sm px-2 font-semibold text-ink-700 hover:bg-surface-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border"
            >
              Annuler
            </button>
          </span>
        )}
      </footer>
    </div>
  );
}

export default MaSemaine;
